import { useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { Icon } from '../Icon.js'
import { Spinner } from '../ui.js'
import { fromLocalInput, sendLaterOptions, toLocalInput, whenLabel } from './text.js'
import { useDismiss } from './useDismiss.js'

/**
 * Send later. The times are the account's clock, which is the customer's,
 * whatever the phone in somebody's pocket thinks. The worker sends it through
 * the same procedure as Send. Decision 95.
 */
export function SendLaterMenu({
  timeZone,
  scheduledFor,
  busy,
  disabled,
  onSchedule,
}: {
  timeZone: string
  /** Set when the window is editing a message already scheduled. */
  scheduledFor?: Date | null
  busy: boolean
  disabled?: boolean
  onSchedule: (at: Date) => void
}) {
  const [open, setOpen] = useState(false)
  const [picking, setPicking] = useState(false)
  const [value, setValue] = useState('')
  const wrap = useRef<HTMLDivElement>(null)
  const close = () => {
    setOpen(false)
    setPicking(false)
  }
  useDismiss(wrap, open, close)

  const now = new Date()
  const options = sendLaterOptions(now, timeZone)
  const picked = fromLocalInput(value, timeZone)
  const tooSoon = picked !== null && picked.getTime() < Date.now() + 60_000

  const choose = (at: Date) => {
    onSchedule(at)
    close()
  }

  return (
    <div ref={wrap} className="relative">
      <button
        type="button"
        aria-expanded={open}
        disabled={disabled || busy}
        className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg bg-white px-3.5 text-[14px] whitespace-nowrap font-medium text-ink-800 ring-1 ring-ink-300 ring-inset hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => (open ? close() : setOpen(true))}
      >
        {busy ? <Spinner className="size-4" /> : null}
        {scheduledFor ? whenLabel(scheduledFor, timeZone) : 'Send later'}
        <Icon name="chevronDown" className="size-3.5 text-ink-400" />
      </button>
      {open ? (
        <div
          role="menu"
          aria-label="Send later"
          className="absolute right-0 bottom-full z-30 mb-2 w-72 overflow-hidden rounded-xl bg-white p-1 shadow-xl ring-1 ring-ink-200"
        >
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              role="menuitem"
              className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-[13px] text-ink-800 hover:bg-ink-50"
              onClick={() => choose(option.at)}
            >
              <span className="font-medium">{option.label}</span>
              <span className="text-[12px] text-ink-400">{whenLabel(option.at, timeZone).split(',')[0]}</span>
            </button>
          ))}
          {picking ? (
            <div className="flex flex-col gap-2 border-t border-ink-100 p-2">
              <input
                type="datetime-local"
                aria-label="Send at"
                value={value}
                min={toLocalInput(now, timeZone)}
                className="w-full rounded-md bg-ink-50 px-2.5 py-1.5 text-[13px] ring-1 ring-ink-200 outline-none ring-inset focus:ring-ink-400"
                onChange={(event) => setValue(event.target.value)}
              />
              {tooSoon ? <p className="text-[12px] text-red-700">Pick a time at least a minute from now.</p> : null}
              <button
                type="button"
                disabled={!picked || tooSoon}
                className={cn(
                  'rounded-md bg-brand-800 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-brand-700',
                  'disabled:cursor-not-allowed disabled:opacity-40',
                )}
                onClick={() => picked && choose(picked)}
              >
                {picked && !tooSoon ? `Send ${whenLabel(picked, timeZone)}` : 'Schedule'}
              </button>
            </div>
          ) : (
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center rounded-md px-3 py-2 text-left text-[13px] font-medium text-ink-800 hover:bg-ink-50"
              onClick={() => {
                setValue(toLocalInput(options[0]!.at, timeZone))
                setPicking(true)
              }}
            >
              Pick a time…
            </button>
          )}
        </div>
      ) : null}
    </div>
  )
}
